// ---------------------------------------------------------------------------
// Inlined from ./page-meta.ts
// Imports repointed from ../constants/app.js → ../constants/index.js
// (app.ts was deleted when the constants barrel was flattened).
// ---------------------------------------------------------------------------

import { APP_DESCRIPTION, APP_NAME, APP_TAGLINE } from '../constants/index.js';
import { GUIDES } from '../guides/index.js';

export type PageMeta = {
  /** Pathname that this meta applies to, e.g. "/pricing". */
  path: string;
  /** Page title without the trailing " · Visualry" suffix. */
  title: string;
  /** Meta description, 130-160 chars. Used in <meta name="description"> and og:description. */
  description: string;
  /**
   * Path to the OG image, relative to PUBLIC_APP_URL. Defaults to
   * "/og-default.png" when omitted. The Worker dispatcher and the
   * <SeoHead> component both resolve this against PUBLIC_APP_URL.
   */
  ogImage?: string;
  /**
   * When true, the page is excluded from indexing. The Worker dispatcher
   * adds `<meta name="robots" content="noindex, follow">` and the
   * <SeoHead> component does the same. Search results and other
   * near-duplicate surfaces should set this.
   */
  noindex?: boolean;
  /**
   * Optional JSON-LD blocks to embed. Each entry is a schema.org object.
   * Multiple blocks are emitted as multiple <script type="application/ld+json"> tags.
   * The factory receives the canonical public app URL so it can build
   * absolute `url` / `logo` fields.
   */
  jsonLd?: (publicAppUrl: string) => unknown[];
};

const DEFAULT_OG_IMAGE = '/og-default.png';

export const resolveOgImageUrl = (path: string | undefined, publicAppUrl: string): string => {
  const value = path ?? DEFAULT_OG_IMAGE;
  return new URL(value, publicAppUrl).toString();
};

const orgJsonLd = (publicAppUrl: string) => [
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: APP_NAME,
    url: `${publicAppUrl}/`,
    description: APP_DESCRIPTION,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${publicAppUrl}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: APP_NAME,
    url: `${publicAppUrl}/`,
    logo: resolveOgImageUrl(DEFAULT_OG_IMAGE, publicAppUrl),
    description: APP_DESCRIPTION,
  },
];

// ---------------------------------------------------------------------------
// FAQ data lives here so the React /faq page and the Worker dispatcher's
// FAQPage JSON-LD can render the same questions and answers.
// ---------------------------------------------------------------------------

export type FaqEntry = { question: string; answer: string };

export const FAQ_ENTRIES: ReadonlyArray<FaqEntry> = [
  {
    question: 'What is Visualry?',
    answer:
      'Visualry is a curated AI image and prompt library. You can browse images, reveal prompts, save favorites, and use credits to generate new visuals.',
  },
  {
    question: 'Do I need to pay to view prompts?',
    answer:
      'No. Prompt browsing, reveals, copying, bookmarks, and collections are available on the free account.',
  },
  {
    question: 'What are credits?',
    answer:
      'Credits are used for image generation. Standard text-to-image generation uses 4 credits, and standard generation with a reference image uses 8 credits.',
  },
  {
    question: 'Why does image-to-image cost double?',
    answer:
      'Reference images require the AI model to process your uploaded image in addition to the prompt. That extra processing is priced at 2x the matching text-only generation.',
  },
  {
    question: 'How many credits do Plus and Pro include?',
    answer:
      'Plus includes 350 credits per month. Pro includes 1,000 credits per month. Subscription credits reset at the start of each billing month and do not carry forward.',
  },
  {
    question: 'What happens if a generation fails?',
    answer:
      'Failed or timed-out generations restore the credits automatically, so your balance returns to what it was before the attempt.',
  },
  {
    question: 'What content is not allowed?',
    answer:
      'Visualry does not allow nude, pornographic, obscene, sexualized, non-consensual intimate, or minor-related sexual image generation. You are responsible for prompts, uploads, and outputs you choose to use.',
  },
  {
    question: 'Do blocked policy attempts use credits?',
    answer:
      'No. Requests blocked by Visualry before generation do not create a job and do not consume credits. If a provider blocks a request after a job starts, Visualry marks it failed and restores the credits.',
  },
  {
    question: 'Can I buy credits without subscribing?',
    answer:
      'Yes. One-time credit packs are available from Pricing and Account for users who do not want a subscription or need extra credits.',
  },
  {
    question: 'What is the difference between subscription credits and credit packs?',
    answer:
      'Subscription credits reset each billing period and do not carry forward. Credit packs are one-time top-ups added to your account balance for extra usage.',
  },
  {
    question: 'Can I use my generated images commercially?',
    answer:
      'Commercial usage depends on the model and plan terms shown at checkout. Pro is intended for commercial workflows.',
  },
  {
    question: 'Who is responsible for generated images?',
    answer:
      'You are responsible for the prompts, uploads, and output you choose to use. Visualry provides the generation service, but you must review every result and confirm it is appropriate for your intended use before publishing or relying on it.',
  },
  {
    question: 'Can I upload a reference image?',
    answer:
      'Yes. Reference uploads can guide style, layout, product appearance, or edits from a previous generation.',
  },
];

const faqJsonLd = (publicAppUrl: string) => [
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ENTRIES.map((entry) => ({
      '@type': 'Question',
      name: entry.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: entry.answer,
      },
    })),
    url: `${publicAppUrl}/faq`,
    isPartOf: {
      '@type': 'WebSite',
      name: APP_NAME,
      url: publicAppUrl,
    },
  },
];

// ---------------------------------------------------------------------------
// Static page meta. The Worker dispatcher and the <SeoHead> component
// both consume this list. JSON-LD factories receive publicAppUrl so they
// can build absolute URLs that match the Worker's canonical links.
// ---------------------------------------------------------------------------

export const STATIC_PAGE_META: ReadonlyArray<PageMeta> = [
  {
    path: '/',
    title: APP_TAGLINE,
    description: APP_DESCRIPTION,
    jsonLd: orgJsonLd,
  },
  {
    path: '/explore',
    title: 'Explore AI image prompts',
    description:
      'Browse the Visualry prompt atlas: trending, popular, most-copied, and newest AI image prompts across every category. Free account unlocks the full text of every prompt.',
  },
  {
    path: '/categories',
    title: 'AI image prompt categories',
    description:
      'Browse every Visualry category — headshots, fashion, brand ads, film posters, product photography, and more. Each category links to curated AI prompts and example images.',
  },
  {
    path: '/search',
    title: 'Search the prompt library',
    description:
      'Search the Visualry library for AI image prompts by style, model, category, or tag.',
    noindex: true,
  },
  {
    path: '/pricing',
    title: 'Pricing — Free, Plus, and Pro plans',
    description:
      'Visualry pricing: free unlimited prompt access, Plus with 350 monthly generation credits, Pro with 1,000, and one-time credit packs. No paywall to reveal prompts.',
  },
  {
    path: '/credits',
    title: 'Credits and how they work',
    description:
      'How Visualry credits are spent on image generation, why reference images cost double, and how Plus and Pro monthly credits renew each billing period.',
  },
  {
    path: '/faq',
    title: 'Frequently asked questions',
    description:
      'Answers about Visualry plans, credits, prompt reveal, image generation, content policy, and commercial usage.',
    jsonLd: faqJsonLd,
  },
  {
    path: '/terms',
    title: 'Terms of service',
    description: 'The rules for using Visualry, generating images, and managing your account.',
  },
  {
    path: '/privacy',
    title: 'Privacy policy',
    description: 'What Visualry collects, how the data is used, and when it is shared.',
  },
  {
    path: '/refund-policy',
    title: 'Refund policy',
    description:
      'How Visualry handles refunds, cancellations, and billing disputes for credit packs and subscriptions.',
  },
  {
    path: '/showcase',
    title: 'Showcase — AI prompt results',
    description:
      'See what Visualry prompts can do. Side-by-side comparisons of reference images and AI-generated results across different models and styles.',
  },
  {
    path: '/contact',
    title: 'Contact Visualry support',
    description:
      'Find help for accounts, credits, plans, generation issues, and content policy, with quick links to credits, FAQ, and pricing.',
  },
  {
    path: '/guides',
    title: 'Visualry guides — how to write AI prompts that work',
    description:
      'Step-by-step guides for writing AI image prompts: fashion, product photography, headshots, film posters, brand consistency, and reference images. Free, plain-English, with copy-paste prompt templates.',
    jsonLd: (publicAppUrl: string) => [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Visualry AI prompt guides',
        description:
          'Step-by-step guides for writing AI image prompts: fashion, product photography, headshots, film posters, brand consistency, and reference images.',
        url: `${publicAppUrl}/guides`,
        isPartOf: {
          '@type': 'WebSite',
          name: APP_NAME,
          url: publicAppUrl,
        },
        hasPart: GUIDES.map((g) => ({
          '@type': 'Article',
          headline: g.title,
          description: g.summary,
          url: `${publicAppUrl}${g.path}`,
        })),
      },
    ],
  },
  ...GUIDES.map((g) => ({
    path: g.path,
    title: g.title,
    description: g.description,
  })),
];

const STATIC_PATHS = new Set(STATIC_PAGE_META.map((m) => m.path));

export const isKnownStaticPath = (pathname: string): boolean =>
  STATIC_PATHS.has(normalizePath(pathname));

const normalizePath = (pathname: string): string => {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname;
};

export const findStaticPageMeta = (pathname: string): PageMeta | null => {
  const normalized = normalizePath(pathname);
  return STATIC_PAGE_META.find((m) => m.path === normalized) ?? null;
};

export const FULL_TITLE_SUFFIX = ` · ${APP_NAME}`;

export const buildFullTitle = (pageTitle: string): string => {
  if (pageTitle.endsWith(APP_NAME)) return pageTitle;
  return `${pageTitle}${FULL_TITLE_SUFFIX}`;
};
